import type { StudioView } from './studioViewHistory'
import { CREW_DIRECTORY_URL } from '../components/portal/studioNavParts'

/**
 * The tiles on the studio front page, and the photographs behind them.
 *
 * **4 Nov 2026.** Tom, after seeing Portal LA's catalogue site: a front page
 * of image tiles rather than a line of text links. They are the BODY of the
 * page — see the note in pages/portal/Home.tsx about the first attempt,
 * which hid them under the week and shipped a front page that looked
 * identical to the day before's.
 *
 * **The photographs are Portal's own frames.** The rooftop interview, the two
 * crew silhouettes, the monitor wall, the flight cases. That is the whole
 * point of them: a stock picture of a mixing desk would say nothing this
 * studio has not already said better.
 *
 * **THE SIZE RULE: 760px, JPEG, under about 60KB.** The originals Tom first
 * uploaded were 6.9MB for the set, which is not a front page, it is a
 * download. Ten images load at once here, so one careless frame costs more
 * than all the careful ones together — the call-sheet frame spent an hour
 * at 587KB as the original PNG before it was swapped. **Resize before
 * uploading, every time.**
 *
 * **Uploading, since it catches people out:** Supabase Storage will not
 * overwrite an object from the dashboard. Delete the old file first, then
 * upload. And check the name that actually lands — a browser that has
 * downloaded the file before will have renamed it (`callsheets_1.jpg`
 * below is exactly that).
 *
 * **No AI images, ever, and no stripe or fake-grain overlays** — both are
 * standing instructions. The grading below is CSS over the real file, so
 * nothing is baked and a replacement frame needs no retouching.
 *
 * ---
 *
 * **VARY THE SUBJECT, NOT JUST THE TONE (5 Nov 2026).** The first set was
 * ordered so that light and dark alternated, which is worth doing and is
 * not enough on its own. Tom: *"what would make a good (darkened) call
 * sheet image — this one is too similar to crew and clients"*. He was
 * right, and the reason no grading would have fixed it: Crew, Clients and
 * Call sheets were all **people on a location**. Three of eight tiles with
 * the same subject read as the same tile however they are graded.
 *
 * Call sheets is now the empty set — lights up, nobody in frame. It is the
 * only one of the three with no people in it, and it is what a call sheet
 * actually is: a day that has not happened yet. **When the Calendar frame
 * is finally replaced, check it against this rule too**, not just against
 * its own brightness.
 *
 * **The Calendar frame: leave the grading alone.** It is the one picture
 * here that is not Portal's own, and when Tom called it "stuck in there
 * like clipart" the fix tried was to crop hard into the grid
 * (`backgroundSize: 190%`) and crush it to contrast 1.85 / brightness 0.46.
 * That made it worse — a grey smear of enlarged numerals with no subject,
 * which is what a photograph becomes when you zoom past its composition to
 * hide what it is of. Reverted at his request to the original grading, and
 * the real answer is still a replacement frame: anything with depth and a
 * light source — a schedule on a monitor, a production-office wall.
 */

/** The public bucket. Anything here is world-readable by design — brand
 *  furniture only, never client work. */
const BRAND =
  'https://fdyfqzqogibrsqxueeaj.supabase.co/storage/v1/object/public/Brand'

/** The master shared drive, for the Files tile until the Files tab exists. */
export const MASTER_DRIVE_URL = 'https://drive.google.com/drive/folders/0ANloG66XUxDwUk9PVA'

export interface HomeTile {
  key: string
  label: string
  /**
   * The line under the label. Static descriptions for now — the mock-up had
   * live counts ("14 waiting on us", "2 upcoming") and those want a fetch
   * each, which is a job of its own. `clients` is the exception: the count is
   * already in context, so it is real. Never invent a number here.
   */
  sub: string
  /** The studio view it opens, when it opens one. */
  view?: StudioView
  /** An address instead, for the two destinations that are not views. */
  href?: string
  image: string
  /** Tuned per frame: the pale ones need holding down, the dark ones lifting. */
  filter: string
  /** Background-size, for a frame that needs cropping into. Default cover. */
  size?: string
  /** Owners only, the same rule the Dispatches tab follows. */
  ownerOnly?: boolean
}

const TILES: HomeTile[] = [
  {
    key: 'news',
    label: 'News',
    sub: "Today's round-up",
    view: 'news',
    image: `${BRAND}/news.jpg`,
    filter: 'grayscale(1) contrast(1.2) brightness(1.14)',
  },
  {
    key: 'calendar',
    label: 'Calendar',
    sub: 'The diary',
    view: 'calendar',
    image: `${BRAND}/calendar.jpg`,
    /* Original grading. See the note above before touching this. */
    filter: 'grayscale(1) contrast(1.3) brightness(0.8)',
  },
  {
    key: 'wrapUps',
    label: 'Wrap-ups',
    sub: 'Post-show reports',
    view: 'wrapUps',
    image: `${BRAND}/wrapups.jpg`,
    filter: 'grayscale(1) contrast(1.2) brightness(1.06)',
  },
  {
    key: 'dispatches',
    label: 'Dispatches',
    sub: 'Client email',
    view: 'conversations',
    ownerOnly: true,
    image: `${BRAND}/dispatches.jpg`,
    filter: 'grayscale(1) contrast(1.26) brightness(0.82)',
  },
  {
    key: 'files',
    label: 'Files',
    sub: 'Master drive',
    href: MASTER_DRIVE_URL,
    image: `${BRAND}/files.jpg`,
    filter: 'grayscale(1) contrast(1.32) brightness(0.78)',
  },
  {
    key: 'callSheets',
    label: 'Call sheets',
    sub: 'Shoot days',
    view: 'callSheets',
    /* `_1` is a download artefact, not a version number — the browser had
       already saved a callsheets.jpg, so the second download took that
       name and that is what went into the bucket. Harmless; rename the
       object and this line together if it ever irritates. */
    image: `${BRAND}/callsheets_1.jpg`,
    /* The empty set, lights up, nobody in frame — replaced 5 Nov, see the
       note above. Held DOWN rather than lifted, unlike the frame it
       replaced: the picture is mostly dark floor with one hot pool of light
       on the wall, and lifting it blows the practicals out to paper white.
       Low contrast for the same reason. The caption sits over the dark
       foreground, so it needs no help from the scrim. */
    filter: 'grayscale(1) contrast(1.12) brightness(0.92)',
  },
  {
    key: 'clients',
    label: 'Clients',
    sub: '',
    view: 'clients',
    image: `${BRAND}/clients.jpg`,
    filter: 'grayscale(1) contrast(1.16) brightness(0.86)',
  },
  {
    key: 'crew',
    label: 'Crew',
    sub: 'The directory',
    href: CREW_DIRECTORY_URL,
    image: `${BRAND}/crew.jpg`,
    filter: 'grayscale(1) contrast(1.22) brightness(1.16)',
  },
]

export const HOME_TILES = TILES

/** Looked up by key when the running order is drawn. */
export const TILE_BY_KEY: Record<string, HomeTile> = Object.fromEntries(
  TILES.map((tile) => [tile.key, tile]),
)

/**
 * **THE RUNNING ORDER, INCLUDING THE TWO SQUARES THAT ARE NOT PHOTOGRAPHS.**
 *
 * `NEW_PROJECT` and `SETTINGS` are tokens rather than tiles — they carry
 * drawings rather than frames, and HomeTiles renders them itself. They are
 * in this list because their POSITION is the thing that matters and it has
 * to be decided in one place alongside everything else.
 *
 * The order is Tom's, arrived at by eye over several swaps on 5 Nov. One
 * rule came out of it that is worth keeping when anything moves again:
 *
 * **The two drawn squares must not sit next to each other.** They did at
 * first, together at the end of the second row, and he was right about the
 * result — *"the + New project looks too similar to settings"*. Two
 * thin-grey-line-on-black squares side by side read as a pair of system
 * buttons rather than as two unrelated things. New Project now sits third,
 * in the middle of the top row with a photograph on each side, and Settings
 * is seven squares away in the far corner.
 *
 * Ten cells over five columns is exactly two rows, which is what lets the
 * front page hold one screen — see pages/portal/Home.tsx. A non-owner loses
 * Dispatches and gets nine, which still fits. An ELEVENTH breaks the page.
 */
export const NEW_PROJECT = '__newProject'
export const SETTINGS = '__settings'

export const HOME_ORDER: string[] = [
  'news',
  'calendar',
  NEW_PROJECT,
  'dispatches',
  'crew',
  'files',
  'callSheets',
  'clients',
  'wrapUps',
  SETTINGS,
]
